/**
 * Phase 13 Beat 3 (Feb 2026) — Recommended Jobs panel (Executive side).
 * Lives on Candidate Detail. Uses the Beat 2 matching engine (lazy-warmed) to
 * surface the top-scoring Active jobs for THIS candidate, with a candidate-centric
 * Apply flow. The Executive never has to hop over to Job Library manually.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ApplicationConfirmDialog from "@/components/ApplicationConfirmDialog";
import { formatPostedAge, formatPostedAbsolute } from "@/lib/postedDate";
import {
  Sparkle, MapPin, Buildings, ArrowsClockwise, Warning, CheckCircle, XCircle,
  Link as LinkIcon, ArrowSquareOut, PaperPlaneTilt, Clock,
} from "@phosphor-icons/react";

const BAND_TONE = {
  "Highly Relevant": "bg-emerald-600 text-white",
  "Relevant":        "bg-sky-600 text-white",
  "Consider":        "bg-amber-500 text-white",
  "Low Match":       "bg-slate-500 text-white",
};

export default function RecommendedJobsPanel({ candidateId, canApply, onApplicationChanged }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [minScore, setMinScore] = useState(50);
  const [dialogFor, setDialogFor] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.get(`/candidates/${candidateId}/job-recommendations?min_score=${minScore}&limit=25`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (ex) {
      setError(formatApiError(ex.response?.data?.detail) || "Failed to load recommendations");
      setRows([]); setTotal(0);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [candidateId, minScore]);

  return (
    <div className="space-y-4" data-testid="recommended-jobs-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkle size={18} weight="fill" className="text-indigo-500" />
            <h3 className="text-lg font-semibold text-slate-900">Recommended Jobs</h3>
            <span className="text-[11px] px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full font-medium">
              Beat 2 engine
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Live scores from the Job Library against this candidate's profile. Only jobs at or above the score threshold are shown.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500 inline-flex items-center gap-1">
            Min score
            <select value={minScore}
                     onChange={(e) => setMinScore(parseInt(e.target.value, 10))}
                     data-testid="recommended-min-score"
                     className="text-xs border border-slate-200 rounded px-2 py-1 bg-white">
              <option value={0}>Any</option>
              <option value={50}>50%+</option>
              <option value={70}>70%+</option>
              <option value={85}>85%+</option>
            </select>
          </label>
          <button onClick={load} disabled={busy}
                   data-testid="recommended-refresh"
                   className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
            <ArrowsClockwise size={12} /> {busy ? "Scoring…" : "Refresh"}
          </button>
        </div>
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 rounded p-3 text-sm text-red-700"
             data-testid="recommended-error">
          {error}
        </div>
      )}

      {!busy && !error && rows.length === 0 && (
        <div className="border border-dashed border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500"
             data-testid="recommended-empty">
          No jobs match this candidate at {minScore}% or above.
          Lower the score threshold or add more jobs to the Job Library.
        </div>
      )}

      <div className="grid gap-3" data-testid="recommended-rows">
        {rows.map((r) => (
          <RecommendationRow key={r.job_id} row={r} canApply={canApply}
                                 onApply={() => setDialogFor(r)} />
        ))}
      </div>

      {rows.length > 0 && (
        <div className="text-[11px] text-slate-500 text-right" data-testid="recommended-total">
          {rows.length} of {total} recommendation{total === 1 ? "" : "s"}
        </div>
      )}

      {dialogFor && (
        <ApplicationConfirmDialog
          candidateId={candidateId}
          job={dialogFor}
          onClose={() => setDialogFor(null)}
          onSubmitted={() => {
            setDialogFor(null);
            load();
            onApplicationChanged && onApplicationChanged();
          }}
        />
      )}
    </div>
  );
}

function RecommendationRow({ row, canApply, onApply }) {
  const bandTone = BAND_TONE[row.band] || "bg-slate-500 text-white";
  const applied = row.already_applied;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:border-indigo-300 transition-colors"
         data-testid={`recommended-row-${row.job_id}`}>
      <div className="flex items-start gap-4">
        {/* Score gauge */}
        <div className="relative w-14 h-14 shrink-0"
             data-testid={`recommended-score-${row.job_id}`}>
          <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" stroke="#e2e8f0" strokeWidth="4" />
            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" stroke="#4f46e5" strokeWidth="4"
                  strokeDasharray={`${row.overall_score}, 100`} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-slate-900">
            {row.overall_score}%
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-slate-900" data-testid={`recommended-title-${row.job_id}`}>
              {row.job_title || "Untitled"}
            </h4>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${bandTone}`}
                  data-testid={`recommended-band-${row.job_id}`}>
              {row.band}
            </span>
            {row.review_required && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-800 inline-flex items-center gap-1"
                    data-testid={`recommended-review-${row.job_id}`}>
                <Warning size={10} weight="fill" /> Review Required
              </span>
            )}
            {applied && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-700 inline-flex items-center gap-1"
                    data-testid={`recommended-applied-${row.job_id}`}>
                <CheckCircle size={10} weight="fill" /> Already {row.latest_application_status}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1"><Buildings size={10} /> {row.company || "—"}</span>
            <span className="inline-flex items-center gap-1"><MapPin size={10} /> {row.location || "—"}</span>
            {row.work_arrangement && <span>· {row.work_arrangement}</span>}
            {row.employment_type && <span>· {row.employment_type}</span>}
            {/* Phase 13 Beat 4 UAT Bug 6 — always surface employer's originally-posted date
                so Executives can gauge freshness without opening the JD. */}
            <span className="inline-flex items-center gap-1 text-slate-500"
                  title={formatPostedAbsolute(row.originally_posted_at) || "Employer posting date not available"}
                  data-testid={`recommended-posted-${row.job_id}`}>
              <Clock size={10} /> {formatPostedAge(row.originally_posted_at)}
            </span>
          </div>
          {row.top_matched.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`recommended-matched-${row.job_id}`}>
              {row.top_matched.map((m) => (
                <span key={`${row.job_id}-m-${m}`} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1">
                  <CheckCircle size={9} weight="fill" /> {m}
                </span>
              ))}
            </div>
          )}
          {row.top_gaps.length > 0 && (
            <div className="mt-1.5 space-y-0.5" data-testid={`recommended-gaps-${row.job_id}`}>
              {row.top_gaps.map((g) => (
                <div key={`${row.job_id}-g-${g}`} className="text-[11px] text-amber-700 inline-flex items-center gap-1">
                  <XCircle size={10} weight="fill" /> {g}
                </div>
              ))}
            </div>
          )}
          {row.review_required && row.review_reasons?.length > 0 && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-900"
                 data-testid={`recommended-review-reasons-${row.job_id}`}>
              <div className="font-semibold flex items-center gap-1">
                <Warning size={10} weight="fill" /> Review Required
              </div>
              <ul className="list-disc list-inside">
                {row.review_reasons.map((rn) => <li key={`${row.job_id}-rr-${rn}`}>{rn}</li>)}
              </ul>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 items-end shrink-0">
          <a href={`/jobs/${row.job_id}`} target="_blank" rel="noreferrer"
             data-testid={`recommended-view-jd-${row.job_id}`}
             className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50">
            <LinkIcon size={11} /> View JD
          </a>
          <button onClick={onApply}
                   disabled={!canApply || applied}
                   data-testid={`recommended-apply-${row.job_id}`}
                   className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed">
            <PaperPlaneTilt size={11} weight="fill" />
            {applied ? "Applied" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}
